/**
 * A simply pluralising utility. It can be adapted to handle irregular nouns when needed.
 *
 * @param word The words to be pluralised if needed.
 * @param quantity optional - Determines if the words should be plural.
 */
export const pluralise = (word: string, quantity = 2): string => {
    if (+quantity === 1) return word;

    const irregularNouns = {
        is: "are",
        day: "days",
    };

    if (irregularNouns[word]) return irregularNouns[word];

    // Determine the plural form of the word
    if (word.endsWith("y")) {
        return word.substring(0, word.length - 1) + "ies";
    } else if (
        word.endsWith("ch") ||
        word.endsWith("s") ||
        word.endsWith("sh") ||
        word.endsWith("x") ||
        word.endsWith("z")
    ) {
        return word + "es";
    } else {
        return word + "s";
    }
};

/**
 * The success object returned by the `safelyParseJSON` function.
 */
interface ISafelyParseJSONSuccess<Output> {
    isSuccess: true;
    isError: false;
    data: Output;
    error?: never;
}

/**
 * The error object returned by the `safelyParseJSON` function.
 */
interface ISafelyParseJSONError {
    isSuccess: false;
    isError: true;
    data?: never;
    error: string;
}

/**
 * The result object returned by the `safelyParseJSON` function.
 * If parsing is successful, the object will have the following shape:
 * - `isSuccess: true`
 * - `isError: false`
 * - `data: OutputType` (the parsed data)

* If parsing fails, the object will have the following shape:
 * - `isSuccess: false`
 * - `isError: true`
 * - `error: string` (the error message)
 */
export declare type ISafelyParseJSONResult<Output> = ISafelyParseJSONSuccess<Output> | ISafelyParseJSONError;

/**
 * Safely parses a JSON string.
 *
 * @param str The JSON string to parse.
 * @returns An object representing the result of the parsing operation.
 *
 * If parsing is successful, the object will have the following shape:
 *   - `isSuccess: true`
 *   - `isError: false`
 *   - `data: OutputType` (the parsed data)

 * If parsing fails, the object will have the following shape:
 *   - `isSuccess: false`
 *   - `isError: true`
 *   - `error: string` (the error message)
 *
 * @note This does not check the passed in type, so it is up to the caller to
 * ensure that the type matches the JSON string.
 *
 * @example
 * const json = '{"name": "John", "age": 30}';
 * const result = safelyParseJSON<{ name: string; age: number }>(json);
 *
 * if (result.success) {
 *   console.log(result.data.name); // Output: "John"
 *   console.log(result.data.age);  // Output: 30
 * } else {
 *   console.error(result.error);   // Output: An error message if parsing failed
 * }
 */
export function safelyParseJSON<OutputType>(str: string): ISafelyParseJSONResult<OutputType> {
    try {
        const result: OutputType = JSON.parse(str) as OutputType;
        return { isSuccess: true, isError: false, data: result };
    } catch (error: unknown) {
        const message = error instanceof Error ? error.message : "An error occurred while parsing the JSON string.";
        return { isSuccess: false, isError: true, error: message };
    }
}
